/**
 * OV2 orders list — shared query key, param builder, and fetcher.
 * Used by useOrdersQuery, ordersQueryClient prefetch, and guards.
 */
import { getOrders } from "@/lib/api";
import { normalizeOrderSearch } from "@/lib/orderSearch";

export const ORDERS_QUERY_KEY = "orders";

/** Paginated search — same page size as normal tabs (fast first paint). */
const ORDER_SEARCH_PAGE_SIZE = 20;

export function computeOrdersPageSize({ activeTab, searchQuery, upcomingPageSize, priorityOnly }) {
  const isSearchFetch = Boolean(searchQuery);
  if (priorityOnly) return 100;
  if (isSearchFetch) return ORDER_SEARCH_PAGE_SIZE;
  if (activeTab === "upcoming") return upcomingPageSize;
  return 20;
}

export function buildOrdersQueryParams({
  activeTab,
  page,
  filters,
  upcomingPageSize,
  priorityOnly,
}) {
  const searchQuery = normalizeOrderSearch(filters?.search);
  const isSearchFetch = Boolean(searchQuery);
  const pageSize = computeOrdersPageSize({
    activeTab,
    searchQuery,
    upcomingPageSize,
    priorityOnly,
  });

  const params = {
    page: priorityOnly ? 1 : page,
    limit: pageSize,
    fields: "list",
  };

  if (priorityOnly) {
    params.filter_type = "today";
    params.is_priority = true;
  } else if (isSearchFetch) {
    params.search = searchQuery;
    params.use_fulltext = true;
    if (activeTab === "inquiries") {
      params.is_inquiry = true;
    } else if (activeTab !== "subscriptions") {
      params.filter_type = activeTab;
    }
  } else {
    params.filter_type = activeTab;
    if (activeTab === "inquiries") {
      params.is_inquiry = true;
      delete params.filter_type;
    }
  }

  if (!priorityOnly) {
    if (filters?.start_date) params.start_date = filters.start_date;
    if (filters?.end_date) params.end_date = filters.end_date;
  }

  return params;
}

export async function fetchOrdersFromApi(params, signal) {
  const response = await getOrders(
    params,
    signal ? { signal } : undefined
  );
  return {
    orders: response.data.orders || [],
    total: response.data.total || 0,
  };
}
